import React, { useState } from 'react';
import { userService } from '../../services/userService';

interface CompanyEmailToggleProps {
  userId: number;
  userRole?: number;
  initialValue?: boolean;
  onToggle?: (enabled: boolean) => void;
  disabled?: boolean;
}

export default function CompanyEmailToggle({
  userId,
  userRole,
  initialValue = false,
  onToggle,
  disabled = false
}: CompanyEmailToggleProps) {
  const [isEnabled, setIsEnabled] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only render if user has role 30
  if (userRole !== 30) {
    return null;
  }

  const handleToggle = async () => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const newValue = !isEnabled;
      await userService.updateCompanyEmail(userId, newValue);
      setIsEnabled(newValue);
      onToggle?.(newValue);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update company email setting');
      // Revert the toggle if the API call failed
      setIsEnabled(isEnabled);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center">
      <label className="flex items-center cursor-pointer">
        <div className="relative">
          <input
            type="checkbox"
            className="sr-only"
            checked={isEnabled}
            onChange={handleToggle}
            disabled={disabled || isLoading}
          />
          <div className={`
            block w-14 h-8 rounded-full transition-colors duration-200 ease-in-out
            ${isEnabled ? 'bg-primary' : 'bg-gray-300'}
            ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}>
            <div className={`
              absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-200 ease-in-out
              ${isEnabled ? 'transform translate-x-6' : 'transform translate-x-0'}
            `} />
          </div>
        </div>
      </label>

      {isLoading && (
        <div className="ml-3 text-sm text-gray-500">
          Updating...
        </div>
      )}

      {error && (
        <div className="ml-3 text-sm text-red-500">
          {error}
        </div>
      )}
    </div>
  );
} 